import type { Map } from 'maplibre-gl'
import { ATLAS_PALETTE, PEAK_IMAGE_ID } from '../../lib/atlas_style'

/**
 * The summit mark: a small solid triangle in the spot heights' umber, and the
 * only image on the map.
 *
 * It is drawn here, as pixels, rather than fetched. The style has no sprite on
 * purpose — a sprite is two requests on the boot path for a sheet of icons a
 * wine atlas does not draw — and one triangle does not change that: it is
 * sixteen pixels square, computed once, and handed to the map with `addImage`.
 *
 * Computed without a canvas, so it is the same bytes in a test under jsdom as
 * in a browser: each pixel's coverage is the share of a 4 by 4 grid of samples
 * that fall inside the triangle, which is all the antialiasing a shape this
 * size needs.
 *
 * Types are imported from maplibre-gl, values never — this module is reached
 * only through use_maplibre.ts, which is itself behind the layout's dynamic
 * import.
 */

// Drawn at twice the size it is shown, for a high-density screen.
const PIXEL_RATIO = 2
const SIZE = 8 * PIXEL_RATIO
const SAMPLES = 4

// The triangle, in image pixels: apex up, a little inside the box on every side
// so the antialiased edge is not clipped by it.
const APEX: [number, number] = [SIZE / 2, 1.5 * PIXEL_RATIO]
const RIGHT: [number, number] = [SIZE - PIXEL_RATIO, SIZE - 1.5 * PIXEL_RATIO]
const LEFT: [number, number] = [PIXEL_RATIO, SIZE - 1.5 * PIXEL_RATIO]

const side = ([fromX, fromY]: [number, number], [toX, toY]: [number, number], x: number, y: number) =>
  (toX - fromX) * (y - fromY) - (toY - fromY) * (x - fromX)

function inside(x: number, y: number) {
  const first = side(APEX, RIGHT, x, y)
  const second = side(RIGHT, LEFT, x, y)
  const third = side(LEFT, APEX, x, y)

  return (first >= 0 && second >= 0 && third >= 0) || (first <= 0 && second <= 0 && third <= 0)
}

export interface PeakImage {
  width: number
  height: number
  data: Uint8Array
}

/** The mark as RGBA pixels, not premultiplied — the form `addImage` takes. */
export function peakImage(color: string = ATLAS_PALETTE.labelPeak): PeakImage {
  const [red, green, blue] = [1, 3, 5].map((at) => parseInt(color.slice(at, at + 2), 16))
  const data = new Uint8Array(SIZE * SIZE * 4)

  for (let row = 0; row < SIZE; row++) {
    for (let column = 0; column < SIZE; column++) {
      let covered = 0

      for (let sampleRow = 0; sampleRow < SAMPLES; sampleRow++) {
        for (let sampleColumn = 0; sampleColumn < SAMPLES; sampleColumn++) {
          if (inside(column + (sampleColumn + 0.5) / SAMPLES, row + (sampleRow + 0.5) / SAMPLES)) covered++
        }
      }

      const at = (row * SIZE + column) * 4
      data[at] = red
      data[at + 1] = green
      data[at + 2] = blue
      data[at + 3] = Math.round((covered / (SAMPLES * SAMPLES)) * 255)
    }
  }

  return { width: SIZE, height: SIZE, data }
}

/**
 * Hands the mark to a style that has just loaded. Idempotent, because a style
 * swap discards the previous style's images and `addImage` throws on a
 * duplicate id.
 *
 * Called from `style.load`, which is before the first tile has been parsed and
 * therefore before any summit asks for it. It is also what answers MapLibre's
 * `styleimagemissing` (use_maplibre.ts), for the one order of events in which
 * that is not true.
 */
export function addPeakImage(map: Map) {
  if (map.hasImage(PEAK_IMAGE_ID)) return

  map.addImage(PEAK_IMAGE_ID, peakImage(), { pixelRatio: PIXEL_RATIO })
}
